import React from 'react';
import { jurisdictionName } from './jurisdictions';
import {
  activeQuestion,
  answerLabel,
  clearDownstream,
  licensingQuestions,
  rungRejected,
  type LicensingQuestionId,
} from './flow';
import AnsweredRow from './components/AnsweredRow';
import ChoiceCards from './components/ChoiceCards';
import PathCallout from './components/PathCallout';
import StateSelect from './components/StateSelect';
import { isAvailable, type LicensingAnswers, type LicensingPath } from './types';

/**
 * "Where are you getting licensed?" — the onboarding step (.pen `c3lQkf` /
 * `Uqfca`, #411).
 *
 * Progressive by construction: answered questions collapse into rows, exactly
 * ONE question is active, and the list of questions itself comes from
 * `licensingQuestions(answers, path)` — so the rung options are the state's own
 * ladder and the supervision questions only exist for someone they apply to.
 *
 * A state we have not researched yet (`available:false, reason:no_data`) gets
 * one honest line and no further questions. We do not ask a learner five
 * questions we cannot answer.
 */
export interface LicensingStepProps {
  answers: LicensingAnswers;
  onChange: (answers: LicensingAnswers) => void;
  path: LicensingPath | null;
  isLoading: boolean;
  hasError: boolean;
}

export const LicensingStep: React.FC<LicensingStepProps> = ({
  answers,
  onChange,
  path,
  isLoading,
  hasError,
}) => {
  const questions = licensingQuestions(answers, path);
  const active = activeQuestion(answers, path);
  const stateName = jurisdictionName(answers.jurisdiction);
  // Two different facts, two different cards (backend contract): `no_data` is a
  // state whose board research is not done; `rung_not_offered` is a state we DO
  // hold, which simply does not have the rung that was named.
  const noRecord =
    !!answers.jurisdiction && !isLoading && !!path && !path.available && path.reason === 'no_data';
  const wrongRung = !isLoading && rungRejected(path);

  const answer = (id: LicensingQuestionId, value: string) => {
    // Changing an answer invalidates everything downstream of it: Virginia's
    // rung must never stay attached to Michigan's ladder.
    onChange({ ...clearDownstream(answers, id), [id]: value });
  };

  const edit = (id: LicensingQuestionId) => {
    const cleared = clearDownstream(answers, id);
    delete cleared[id];
    onChange(cleared);
  };

  return (
    <div data-testid="licensing-step">
      <h2 className="text-xl font-display font-bold text-neutral-900 mb-1">
        Where are you getting licensed?
      </h2>
      <p className="text-sm text-neutral-500 mb-4">
        Rules, fees, and whether an exam is even next depend on your state.
      </p>

      <div className="space-y-2">
        {questions.map((question) => {
          const label = answerLabel(question, answers, stateName);
          if (!label || (active && question.id === active.id)) return null;
          return (
            <AnsweredRow
              key={question.id}
              label={question.label}
              value={label}
              testId={`licensing-answered-${question.id}`}
              onEdit={() => edit(question.id)}
            />
          );
        })}
      </div>

      {wrongRung && (
        <p className="mt-4 text-sm text-neutral-600" data-testid="licensing-rung-not-offered">
          {stateName} does not issue that license. Here are the ones it does.
        </p>
      )}

      {active && (
        <div className={questions.length > 1 ? 'mt-4' : ''}>
          {active.id !== 'jurisdiction' && (
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">
              {active.prompt}
            </p>
          )}
          {active.id === 'jurisdiction' ? (
            <StateSelect
              value={answers.jurisdiction}
              onChange={(code) => answer('jurisdiction', code)}
            />
          ) : (
            <ChoiceCards
              name={active.prompt}
              options={active.options}
              value={answers[active.id]}
              onChange={(value) => answer(active.id, value)}
              testIdPrefix={`licensing-${active.id}`}
            />
          )}
        </div>
      )}

      {isLoading && (
        <p className="mt-4 text-sm text-neutral-500" data-testid="licensing-loading">
          Checking {stateName}&apos;s requirements…
        </p>
      )}

      {/* A real state whose board research is not done yet. Not an error, and
          never a derived guess: we say so and let them continue. */}
      {noRecord && (
        <p className="mt-4 text-sm text-neutral-600" data-testid="licensing-no-data">
          We have not checked {stateName} yet. Your study plan is unchanged, and we will add your
          state&apos;s requirements as soon as we have read the board&apos;s own pages.
        </p>
      )}

      {hasError && !path && (
        <p className="mt-4 text-sm text-neutral-600" data-testid="licensing-error">
          We could not reach the requirements service. You can keep going and we will show your path
          later.
        </p>
      )}

      {isAvailable(path) && answers.licenseRung && (
        <div className="mt-4">
          <PathCallout sentence={path.firstSentence} />
        </div>
      )}
    </div>
  );
};

export default LicensingStep;
